import { useCallback } from 'react'

import { EditorProps } from '@tiptap/pm/view'

import {
  useCreateImages,
  useEmbed,
  useNetwork,
} from '@tribeplatform/react-sdk/hooks'

import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { useUploadQueue } from '../../Providers/UploadQueueProvider.js'
import { handlePastedContent, handlePastedFiles } from '../utils/paste.js'

const isTablePasted = (event: ClipboardEvent): boolean => {
  const html = event.clipboardData?.getData('text/html')
  return html ? html.includes('<table') : false
}

export const useHandlePaste = () => {
  const intl = useI18n()
  const { data: network } = useNetwork()
  const { mutateAsync: createImages } = useCreateImages()
  const { addToUploadQueue, removeFromUploadQueue } = useUploadQueue()
  const { asyncQuery: embed } = useEmbed()

  const handlePaste: EditorProps['handlePaste'] = useCallback(
    (view, event, slice) => {
      const items = Array.from(event.clipboardData?.items || [])
      const pastedFiles = items
        .filter(item => item.kind === 'file')
        .map(item => item.getAsFile())
      const imageFiles = pastedFiles.filter(file =>
        file?.type?.startsWith('image/'),
      )

      /**
       * When a table is pasted, we should not handle any accompanying files.
       * Pasting a table from applications like Numbers or Excel may include a file along with the HTML.
       * Therefore, we need to check if a table is being pasted.
       */
      const tablePasted = isTablePasted(event)

      if (imageFiles.length && !tablePasted) {
        handlePastedFiles({
          view,
          files: imageFiles,
          createImages,
          network,
          intl,
          addToUploadQueue,
          removeFromUploadQueue,
        })

        return true
      }

      handlePastedContent(
        view,
        slice,
        createImages,
        network,
        intl,
        addToUploadQueue,
        removeFromUploadQueue,
        embed,
      )

      return false
    },
    [
      createImages,
      network,
      intl,
      addToUploadQueue,
      removeFromUploadQueue,
      embed,
    ],
  )

  return {
    handlePaste,
  }
}
